import { useState, useEffect } from 'react';
import { FlagImage } from '../utils/countryFlags';
import { User } from '../../types';
import { isMaybeVideo } from '../utils/yapUtils';
import { RecentlyActiveBadge } from './RecentlyActiveBadge';

export interface MiniDiscoverCardProps {
  user: User;
  images: string[];
  /** Pulls flag inward — use in Similar Interests paired layout left column. */
  insetFlag?: boolean;
  intervalMs?: number;
  className?: string;
  onClick?: () => void;
  /** Show the "Recently active" badge (Discover only). */
  showActivityBadge?: boolean;
  /**
   * Which corner that badge sits in. Defaults to the top left, which is where
   * every existing caller wants it; Newest Yappers asks for the bottom left.
   * A prop rather than a change to RecentlyActiveBadge's own defaults, because
   * the badge is shared with the Similar Users pair layout here and with the V1
   * swipe deck, and neither of those is moving.
   */
  activityBadgePosition?: 'top-left' | 'bottom-left';
}

/**
 * Miniature photo card — clean photo fill + floating flag only.
 * Name/age/location are rendered by the caller as an external footer row.
 *
 * Sizing: caller must supply explicit dimensions (width + aspectRatio or height).
 * Outer div: position:relative via inline style (bypasses Tailwind cascade conflicts).
 * Inner clip: absolute inset-0 overflow-hidden rounded-2xl.
 * Flag: lives outside the inner clip so it never gets masked, floats above top-right.
 */
export function MiniDiscoverCard({
  user,
  images,
  insetFlag = false,
  intervalMs = 5000,
  className = '',
  onClick,
  showActivityBadge = false,
  activityBadgePosition = 'top-left',
}: MiniDiscoverCardProps) {
  const count = images.length;
  const [currentIndex, setCurrentIndex] = useState(0);
  const [visible, setVisible] = useState(true);

  const safeIndex = count > 0 ? currentIndex % count : 0;
  const currentSrc = count > 0 ? images[safeIndex] : '';
  const nextIndex = count > 1 ? (safeIndex + 1) % count : 0;
  const nextSrc = count > 1 ? images[nextIndex] : '';

  useEffect(() => {
    if (count <= 1) return;
    let swapTimeout: ReturnType<typeof setTimeout> | null = null;
    const interval = setInterval(() => {
      setVisible(false);
      swapTimeout = setTimeout(() => {
        setCurrentIndex(i => (i + 1) % count);
        setVisible(true);
      }, 250);
    }, intervalMs);
    return () => {
      clearInterval(interval);
      if (swapTimeout !== null) clearTimeout(swapTimeout);
    };
  }, [count, intervalMs]);

  return (
    <div
      className={`cursor-pointer w-full h-full ${className}`}
      style={{ position: 'relative' }}
      onClick={onClick}
    >
      {/* Inner clip — rounded corners + overflow-hidden masks photo edges */}
      <div className="absolute inset-0 overflow-hidden rounded-2xl bg-[#1a1a1a]">

        {/* Active photo or video */}
        {currentSrc ? (
          isMaybeVideo(currentSrc) ? (
            <video
              key={currentSrc}
              src={currentSrc}
              autoPlay
              loop
              muted
              playsInline
              draggable={false}
              className="absolute inset-0 w-full h-full object-cover select-none pointer-events-none"
              style={{ opacity: visible ? 1 : 0, transition: 'opacity 0.25s ease' }}
            />
          ) : (
          <img
            src={currentSrc}
            loading="eager"
            alt=""
            draggable={false}
            className="absolute inset-0 w-full h-full object-cover select-none pointer-events-none"
            style={{ opacity: visible ? 1 : 0, transition: 'opacity 0.25s ease' }}
          />
          )
        ) : (
          <div className="absolute inset-0 flex items-center justify-center">
            <span className="text-white/20 text-3xl select-none">?</span>
          </div>
        )}

        {/* Preload next image invisibly */}
        {count > 1 && nextSrc && (
          <img
            src={nextSrc}
            loading="lazy"
            alt=""
            aria-hidden
            draggable={false}
            className="absolute inset-0 w-full h-full object-cover select-none pointer-events-none"
            style={{ opacity: 0 }}
          />
        )}

        {/* Subtle bottom gradient — keeps card edge clean, minimal overlay */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background: 'linear-gradient(to top, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0.10) 40%, transparent 65%)',
          }}
        />
      </div>

      {/* Flag — outside inner clip so it's never masked.
          Positioned at top-right corner like a sticker — partially on card, partially outside. */}
      {(user.countryFlag || user.country) && (
        <div
          className="absolute pointer-events-none"
          style={{
            width: '52px',
            height: 'auto',
            zIndex: 20,
            top: '-12px',
            right: insetFlag ? '4px' : '-8px',
            filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.80))',
            transform: 'rotate(25deg)',
            transformOrigin: 'center center',
          }}
        >
          <FlagImage countryName={user.countryFlag || user.country} className="w-full h-auto" />
        </div>
      )}

      {/* Active recently. Sits outside the inner clip, so the card's rounded
          corners cannot crop it in either position. */}
      {showActivityBadge && (
        <RecentlyActiveBadge
          userId={user.id}
          style={activityBadgePosition === 'bottom-left'
            ? { top: 'auto', bottom: 6 }
            : undefined}
        />
      )}
    </div>
  );
}
